import { useEffect, useState } from 'react'
import { db } from '@/db/schema'

export default function Debug() {
  const [info, setInfo] = useState<Record<string, unknown> | null>(null)
  const [resyncing, setResyncing] = useState(false)
  const [resyncMsg, setResyncMsg] = useState<string | null>(null)

  const refresh = async () => {
    const [cycles, recoveries, sleep, workouts, cursors, profile] = await Promise.all([
      db.cycles.count(),
      db.recoveries.count(),
      db.sleep.count(),
      db.workouts.count(),
      db.cursors.toArray(),
      db.profile.get('me'),
    ])

    const sampleCycle = await db.cycles.orderBy('start').reverse().limit(1).first()
    const sampleRecovery = await db.recoveries.orderBy('day').reverse().limit(1).first()
    const sampleSleep = await db.sleep.orderBy('day').reverse().limit(1).first()
    const sampleWorkout = await db.workouts.orderBy('day').reverse().limit(1).first()

    setInfo({
      counts: { cycles, recoveries, sleep, workouts },
      cursors,
      profile: profile ? { user_id: profile.user_id, first_name: profile.first_name } : null,
      samples: { sampleCycle, sampleRecovery, sampleSleep, sampleWorkout },
    })
  }

  useEffect(() => {
    refresh()
  }, [])

  const wipeAndResync = async () => {
    if (!confirm('Wipe local cache and re-sync everything?')) return
    setResyncing(true)
    setResyncMsg('Clearing…')
    await Promise.all([
      db.cycles.clear(),
      db.recoveries.clear(),
      db.sleep.clear(),
      db.workouts.clear(),
      db.cursors.clear(),
    ])
    setResyncMsg('Re-syncing…')
    const { syncAll } = await import('@/db/sync')
    const result = await syncAll((p) => setResyncMsg(`${p.phase}: ${p.message}`))
    setResyncMsg(`Done. ${result.message}`)
    setResyncing(false)
    refresh()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Debug</h1>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            className="px-3 py-1.5 rounded-md border border-stone-300 hover:bg-stone-100 text-sm"
          >
            Refresh
          </button>
          <button
            onClick={wipeAndResync}
            disabled={resyncing}
            className="px-3 py-1.5 rounded-md border border-red-300 hover:bg-red-50 text-sm text-red-700 disabled:opacity-50"
          >
            Wipe + resync
          </button>
        </div>
      </div>
      {resyncMsg && (
        <div className="text-sm text-stone-700 bg-white border border-stone-200 rounded-md px-3 py-2">
          {resyncMsg}
        </div>
      )}
      <pre className="text-xs bg-stone-50 border border-stone-200 text-stone-700 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">
        {info ? JSON.stringify(info, null, 2) : 'Loading…'}
      </pre>
    </div>
  )
}
